import { useEffect, useMemo, useState } from 'react';
import MaterialTable, { MTableBodyRow } from '@material-table/core';
import { useMsal, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { AddBox, DeleteOutlineOutlined, Edit,} from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { getRefreshToken, getTokenRoles, handleLogout } from '../../common/utilities/AuthHelperUtil';
import { formatDateTime } from '../../common/utilities/CustomFormatUtil';
import tableIcons from '../../common/utilities/tableIcons';
import { agentPortalAlertsReq } from '../../common/services/Services';
import { loginRequest } from '../../auth/authConfig';
import { ErrorComponent, LoadingComponent, ShopfloorHeader, PermissionRequiredComponent } from '../../components';
import appSettings from '../../appSettings';
import DOMPurify from 'dompurify';
import './WebAlerts.scss';
  
const emptyForm = { id: '', updatedAt: '', title: '', body: '', priority: '', author: '', status: 'Active' };
const REQUIRED_ROLE = appSettings.AZURE_INTERNAL_ROLE;

export default function AlertsTable() {
  const authRequest = { ...loginRequest }; 
  const {instance, accounts} = useMsal();
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authorizationChecked, setAuthorizationChecked] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [internalUsername, setInternalUsername] = useState('');
  const updatePreview = () => setPreview({ ...form });
  
  function SafeHtml({ html }) {
    return <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || '') }} />;
  }

  const getAuthorizedToken = async () => {
    const token = (await getRefreshToken(instance, accounts))?.token;

    if (!token) {
      throw new Error('Unable to acquire access token.');
    }

    const hasRequiredRole = getTokenRoles(token).includes(REQUIRED_ROLE);

    setIsAuthorized(hasRequiredRole);
    setAuthorizationChecked(true);

    if (!hasRequiredRole) {
      throw new Error(`Missing required role: ${REQUIRED_ROLE}`);
    }

    return token;
  };

  const refreshAlerts = async () => {
    if (!isAuthorized) return;

    try {
      setLoading(true);
      const token = await getAuthorizedToken();
      setAlerts((await agentPortalAlertsReq(token, 'GET', true)).data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accounts?.length) {
      return;
    }

    let mounted = true;
    const setIfMounted = (callback) => mounted && callback();

    (async () => {
      setLoading(true);

      try {
        const tokenResponse = await getRefreshToken(instance, accounts);
        const token = tokenResponse?.token;

        if (!token) {
          setIfMounted(() => {
            setIsAuthorized(false);
            setAuthorizationChecked(true);
          });
          return;
        }

        setInternalUsername(tokenResponse.idTokenAccount.username);

        const hasRequiredRole = getTokenRoles(token).includes(REQUIRED_ROLE);

        setIfMounted(() => {
          setIsAuthorized(hasRequiredRole);
          setAuthorizationChecked(true);
        });

        if (!hasRequiredRole) {
          return;
        }

        const res = await agentPortalAlertsReq(token, 'GET', true);

        setIfMounted(() => setAlerts(res.data));
      } catch {
        setIfMounted(() => {
          setIsAuthorized(false);
          setAuthorizationChecked(true);
        });
      } finally {
        setIfMounted(() => setLoading(false));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [instance, accounts]);

  const columns = useMemo(
    () => [
      { title: 'Date', field: 'updatedAt', render: (row) => formatDateTime(row.updatedAt), defaultSort: 'desc' },
      { title: 'Title', field: 'title' },
      { title: 'Body', field: 'body' },
      { title: 'Priority', field: 'priority' },
      { title: 'Author', field: 'author' },
      { title: 'Status', field: 'status' },
    ],
    [],
  );

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = { ...(!form.title?.trim() && { title: 'Title is required' }), ...(!form.body?.trim() && { body: 'Body is required' }), ...(!form.priority?.trim() && { priority: 'Priority is required' }), ...(!form.status?.trim() && { status: 'Status is required' })};

    setErrors(next);
    return !Object.keys(next).length;
  };

  const resetModal = () => {
    setPreview(null);
    setErrors({});
    setOpen(true);
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setForm({ ...emptyForm, updatedAt: new Date().toISOString(), author: internalUsername });
    resetModal();
  };

  const openEditModal = (rowData) => {
    setIsEditMode(true);
    setForm({ id: rowData.id, updatedAt: new Date().toISOString(), title: rowData.title || '', body: rowData.body || '', priority: rowData.priority || 'info', author: internalUsername, status: rowData.status || 'Active' });
    resetModal();
  };

  const closeModal = () => {
    if (busy) {
      return;
    }
    setPreview(null);
    setOpen(false);
    setForm(emptyForm);
    setErrors({});
  };

  const saveModal = async () => {
    if (!validate()) {
      return;
    }

    setBusy(true);
    try {
      const token = await getAuthorizedToken();      
      if (isEditMode) {
        //update
        await agentPortalAlertsReq(token, 'PATCH', true, form.id, { updatedAt: form.updatedAt, title: form.title, body: form.body, priority: form.priority, author: form.author, status: form.status, });
      } else {
        //create
        await agentPortalAlertsReq(token, 'POST', true, null, { updatedAt: form.updatedAt, title: form.title, body: form.body, priority: form.priority, author: form.author, status: form.status });
      }

      await refreshAlerts();
      closeModal();
    } finally {
      setBusy(false);
    }
  };
  
  const handleDeleteConfirm = async (rowData) => {
    setDeleteDialogOpen(true);
    await setDeleteRow(rowData);
  };

  const handleDelete = async (rowData) => {
    setBusy(true);
    try {
      setLoading(true);
      const token = await getAuthorizedToken();
      if (!deleteRow?.id) {
          return;
      }
      await agentPortalAlertsReq(token, 'DELETE', true, deleteRow?.id);
      await refreshAlerts();

      setDeleteDialogOpen(false);
      setDeleteRow(null);
    } finally {
      setBusy(false);
      setLoading(false);
    }
  };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
      errorComponent={ErrorComponent}
      loadingComponent={LoadingComponent}>
        <ShopfloorHeader showInternalHeader={true} showMinifiedHeader={true} internalUserName={internalUsername}/>
        <Box className="alerts-root">
          {authorizationChecked && !isAuthorized ? (
            <PermissionRequiredComponent
              open={true}
              onLogout={() => handleLogout(instance, accounts)}
            />
          ) : null}
          {authorizationChecked && isAuthorized ? (
          <>
            <Dialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
              fullWidth 
              maxWidth="md"
            >
              <DialogTitle>Delete Alert</DialogTitle>
              <DialogContent>
                  Are you sure you want to delete this alert?
              </DialogContent>
              <DialogActions>
                  <Button onClick={() => setDeleteDialogOpen(false)}>
                      Cancel
                  </Button>

                  <Button
                      color="error"
                      onClick={handleDelete}
                  >
                      Delete
                  </Button>
              </DialogActions>
            </Dialog>
            <Box className='alerts-container'>
              <MaterialTable
                title="Manage Alerts"
                style={{ zIndex: 0 }}
                isLoading={loading}
                icons={tableIcons}
                columns={columns}
                data={alerts}
                actions={[
                  { icon: () => <AddBox />, tooltip: 'Add Alert', isFreeAction: true, onClick: openAddModal },
                  { icon: () => <Edit />, tooltip: 'Edit Alert', onClick: (event, rowData) => openEditModal(rowData) },
                  { icon: () => <DeleteOutlineOutlined />, tooltip: 'Delete Alert', onClick: (event, rowData) => handleDeleteConfirm(rowData) },
                ]}
                components={{
                  Row: (props) => <MTableBodyRow {...props} className="manage-alerts-row" />,
                }}
                options={{
                  actionsColumnIndex: -1,
                  paging: true,
                  pageSize: 10,
                  pageSizeOptions: [10, 20, 50],
                  search: true,
                  tableLayout: 'fixed',
                }}
              />
            </Box>
            <Dialog open={open} onClose={closeModal} fullWidth maxWidth="lg">
              <DialogTitle>{isEditMode ? 'Edit Alert' : 'Add Alert'}</DialogTitle>
              <DialogContent>
                <Box className="alerts-dialogLayout">
                  <Box className="alerts-dialogLeft">
                    <TextField
                      margin="dense"
                      label="Date"
                      fullWidth
                      value={form.updatedAt ? formatDateTime(form.updatedAt) : ''}
                      InputProps={{
                        readOnly: true,
                        style: { color: '#808080' }
                      }}
                      disabled={busy}
                    />

                    <TextField
                      margin="dense"
                      label="Author"
                      fullWidth
                      value={form.author}
                      InputProps={{
                        readOnly: true,
                        style: { color: '#808080' }
                      }}
                    />
                    
                    <TextField
                      margin="dense"
                      label="Title"
                      fullWidth
                      value={form.title}
                      onChange={setField('title')}
                      error={!!errors.title}
                      helperText={errors.title || ''}
                      disabled={busy}
                    />

                    <TextField
                      margin="dense"
                      label="Body"
                      fullWidth
                      multiline
                      minRows={3}
                      value={form.body}
                      onChange={setField('body')}
                      error={!!errors.body}
                      helperText={errors.body || ''}
                      disabled={busy}
                    />

                    <TextField
                      margin="dense"
                      label="Priority"
                      fullWidth
                      select
                      value={form.priority}
                      onChange={setField('priority')}
                      error={!!errors.priority}
                      helperText={errors.priority || ''}
                      disabled={busy}
                    >
                      <MenuItem value="info">info</MenuItem>
                      <MenuItem value="critical">critical</MenuItem>
                    </TextField>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.status === 'Active'}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, status: e.target.checked ? 'Active' : 'Inactive'}))
                          }
                          disabled={busy}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  </Box>

                  {/* RIGHT: Preview */}
                  <Box className="alerts-previewPanel">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={updatePreview}
                      disabled={busy}
                      className="alerts-previewUpdateBtn"
                    >
                      Update Preview
                    </Button>

                    <Box className="alerts-previewBody">
                      {!preview ? (
                        <Box className="alerts-previewEmpty">Preview will appear here.</Box>
                      ) : (
                        <Box className="alerts-previewCard">
                          <Box
                            className={[
                              'alerts-previewAccent',
                              preview.priority === 'critical'
                                ? 'alerts-previewAccentCritical'
                                : preview.priority === 'info'
                                ? 'alerts-previewAccentInfo'
                                : '',
                            ].join(' ')}
                          >
                            <Box className="alerts-previewHeaderRow">
                              <Box />
                              <Box className="alerts-previewTitle">
                                 {preview?.title ? (
                                <SafeHtml html={preview.title} />
                                  ) : (
                                    <Box className="alerts-previewEmpty">(No title)</Box>
                                  )}
                              </Box>
                            </Box>

                            <Box className="alerts-previewContent">
                              {preview?.body ? (
                                <SafeHtml html={preview.body} />
                              ) : (
                                <Box className="alerts-previewEmpty">(No body)</Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button onClick={closeModal} disabled={busy}>
                  Cancel
                </Button>
                <Button color="primary" variant="contained" onClick={saveModal} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : null}
      </Box>
    </MsalAuthenticationTemplate>
  );
}
